'use client';

import { c as _c } from "react/compiler-runtime";
import * as Clerk from '@clerk/nextjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@/components/icon';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { matchBrandFromHostname } from '@/features/brand/utils';
import { isSatelliteHost } from './domain';
import { buildPrimaryAuthUrl } from './primary-auth';
const CLERK_UI_GRACE_MS = 450;
type SignUpModalProps = {
  isOpen: boolean;
  onClose: () => void;
};
export function SignUpModal(t0) {
  const $ = _c(48);
  const {
    isOpen,
    onClose
  } = t0;
  const {
    isSignedIn,
    isLoaded
  } = Clerk.useAuth();
  const [mounted, setMounted] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const [isGraceLoading, setIsGraceLoading] = useState(false);
  const didNavigateRef = useRef(false);
  const graceTimerRef = useRef(null);
  const host = mounted ? window.location.host : "";
  let t1;
  if ($[0] !== host) {
    t1 = host ? matchBrandFromHostname(host) : "christspiracy";
    $[0] = host;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const brandId = t1;
  const isBlocked = isFinishing || !isLoaded || isGraceLoading;
  let t2;
  if ($[2] !== isBlocked || $[3] !== onClose) {
    t2 = e => {
      if (isBlocked) {
        return;
      }
      if (e.key === "Escape") {
        onClose();
      }
    };
    $[2] = isBlocked;
    $[3] = onClose;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleEscape = t2;
  let t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setMounted(true);
    };
    t4 = [];
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[7] !== isOpen) {
    t5 = () => {
      if (!isOpen) {
        setIsFinishing(false);
        setIsGraceLoading(false);
        didNavigateRef.current = false;
        if (graceTimerRef.current !== null) {
          window.clearTimeout(graceTimerRef.current);
          graceTimerRef.current = null;
        }
        return;
      }
      setIsGraceLoading(true);
      graceTimerRef.current = window.setTimeout(() => {
        setIsGraceLoading(false);
        graceTimerRef.current = null;
      }, CLERK_UI_GRACE_MS);
      return () => {
        if (graceTimerRef.current !== null) {
          window.clearTimeout(graceTimerRef.current);
          graceTimerRef.current = null;
        }
      };
    };
    t6 = [isOpen];
    $[7] = isOpen;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[10] !== isOpen || $[11] !== isSignedIn || $[12] !== mounted || $[13] !== onClose) {
    t7 = () => {
      if (!mounted) {
        return;
      }
      if (!isOpen) {
        return;
      }
      if (!isSignedIn) {
        return;
      }
      if (new URL(window.location.href).pathname === "/auth-sync") {
        return;
      }
      if (didNavigateRef.current) {
        return;
      }
      didNavigateRef.current = true;
      setIsFinishing(true);
      onClose();
      requestAnimationFrame(_temp);
    };
    t8 = ["/auth-sync?redirect_url=/browse", isOpen, isSignedIn, mounted, onClose];
    $[10] = isOpen;
    $[11] = isSignedIn;
    $[12] = mounted;
    $[13] = onClose;
    $[14] = t7;
    $[15] = t8;
  } else {
    t7 = $[14];
    t8 = $[15];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[16] !== isOpen) {
    t9 = () => {
      if (isOpen) {
        document.body.style.overflow = "hidden";
      }
      return _temp2;
    };
    t10 = [isOpen];
    $[16] = isOpen;
    $[17] = t10;
    $[18] = t9;
  } else {
    t10 = $[17];
    t9 = $[18];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[19] !== handleEscape || $[20] !== isBlocked || $[21] !== isOpen) {
    t11 = () => {
      if (!isOpen) {
        return;
      }
      if (isBlocked) {
        return;
      }
      document.addEventListener("keydown", handleEscape);
      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
    };
    t12 = [handleEscape, isBlocked, isOpen];
    $[19] = handleEscape;
    $[20] = isBlocked;
    $[21] = isOpen;
    $[22] = t11;
    $[23] = t12;
  } else {
    t11 = $[22];
    t12 = $[23];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[24] !== host || $[25] !== isOpen || $[26] !== mounted) {
    t13 = () => {
      if (!mounted || !isOpen) {
        return;
      }
      if (!isSatelliteHost(host, process.env.NODE_ENV === "development" ? "development" : "production")) {
        return;
      }
      const absoluteReturnTo = new URL("/auth-sync?redirect_url=/browse", window.location.origin).toString();
      const signupBrandId = matchBrandFromHostname(host);
      const url = buildPrimaryAuthUrl({
        action: "sign-up",
        redirectUrl: absoluteReturnTo,
        signupBrandId
      });
      window.location.assign(url);
    };
    t14 = ["/auth-sync?redirect_url=/browse", host, isOpen, mounted];
    $[24] = host;
    $[25] = isOpen;
    $[26] = mounted;
    $[27] = t13;
    $[28] = t14;
  } else {
    t13 = $[27];
    t14 = $[28];
  }
  useEffect(t13, t14);
  if (!isOpen || !mounted) {
    return null;
  }
  if (isSatelliteHost(host, process.env.NODE_ENV === "development" ? "development" : "production")) {
    return null;
  }
  let t15;
  if ($[29] !== isBlocked || $[30] !== onClose) {
    t15 = () => {
      if (isBlocked) {
        return;
      }
      onClose();
    };
    $[29] = isBlocked;
    $[30] = onClose;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Icon name="close" className="size-6" />;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== isBlocked || $[34] !== t15) {
    t17 = <Button variant="ghost" size="icon" onClick={t15} disabled={isBlocked} className="absolute -top-12 right-0 text-white hover:bg-white/10">{t16}</Button>;
    $[33] = isBlocked;
    $[34] = t15;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== isBlocked || $[37] !== isFinishing) {
    t18 = isBlocked && <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/70 px-8 py-10 text-center text-white"><div className="flex flex-col items-center justify-center gap-3"><Spinner className="size-6" /><p className="font-medium text-sm uppercase tracking-[0.06rem] opacity-90">{isFinishing ? "Finishing setup..." : "Loading..."}</p></div></div>;
    $[36] = isBlocked;
    $[37] = isFinishing;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  const t19 = isBlocked ? "pointer-events-none select-none opacity-70" : undefined;
  let t20;
  if ($[39] !== brandId) {
    t20 = <Clerk.SignUp routing="hash" fallbackRedirectUrl="/auth-sync?redirect_url=/browse" signInFallbackRedirectUrl="/auth-sync?redirect_url=/browse" unsafeMetadata={{
      signup_brand_id: brandId
    }} />;
    $[39] = brandId;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== t19 || $[42] !== t20) {
    t21 = <div className={t19}>{t20}</div>;
    $[41] = t19;
    $[42] = t20;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== t17 || $[45] !== t18 || $[46] !== t21) {
    t22 = createPortal(<div className="fixed inset-0 z-9999 flex items-center justify-center bg-black/70 backdrop-blur-sm"><div className="relative min-h-[560px] w-[min(460px,92vw)]">{t17}{t18}{t21}</div></div>, document.body);
    $[44] = t17;
    $[45] = t18;
    $[46] = t21;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  return t22;
}
function _temp2() {
  document.body.style.overflow = "";
}
function _temp() {
  window.location.assign("/auth-sync?redirect_url=/browse");
}